import { useMutation, useQuery } from "@apollo/client";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import AutoAwesome from "@mui/icons-material/AutoAwesome";
import DragIndicator from "@mui/icons-material/DragIndicator";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { gql } from "generated-graphql";
import { Fragment, useMemo, useState } from "react";

import { useReorderAnimation } from "./useReorderAnimation";
import { UNPAIRED_TRAY, type PairingDnd, useWasteLinePairingDnd } from "./useWasteLinePairingDnd";
import {
  LINE_DETAIL_FIELDS,
  type AlignmentLine,
  type AlignmentState,
  NO_VENDOR_LINE,
  PAIR_FIELDS,
  type PairingChoices,
  applyPairs,
  canRecordPairing,
  chooseVendorLine,
  differingFields,
  duplicateVendorLines,
  initialChoices,
  pairsToSave,
  pairingRows,
  placeVendorLine,
  unclaimedVendorLines,
  unpairVendorLine,
} from "./WasteLinePairingLedger.helpers";
import { pairingLegendSx, pairingListSx } from "./wasteLinePairingSx";

/** The manifest the matching dialog is for. */
export interface WasteLineMatchingTarget {
  processedManifestId: string;
  manifestTrackingNumber?: string | null;
}

export const VENDOR_WASTE_LINE_ALIGNMENT_QUERY = gql(`
  query VendorWasteLineAlignmentCandidates($processedManifestId: ID!) {
    vendorWasteLineAlignmentCandidates(processedManifestId: $processedManifestId) {
      processedManifestId
      confirmedAt
      confirmedPairs {
        eManifestLine
        vendorLine
      }
      suggestedPairs {
        eManifestLine
        vendorLine
      }
      suggestionIsUnique
      eManifestLines {
        wasteLineNumber
        quantity
        quantityUnitOfMeasureCode
        containerTypeCode
        containerCount
        managementMethodCode
        federalWasteCodes
        stateWasteCodes
        description
      }
      vendorLines {
        wasteLineNumber
        quantity
        quantityUnitOfMeasureCode
        containerTypeCode
        containerCount
        managementMethodCode
        federalWasteCodes
        stateWasteCodes
        description
      }
    }
  }
`);

export const SET_VENDOR_WASTE_LINE_ALIGNMENT = gql(`
  mutation SetVendorWasteLineAlignment($processedManifestId: ID!, $pairs: [VendorWasteLinePairInput!]!) {
    setVendorWasteLineAlignment(processedManifestId: $processedManifestId, pairs: $pairs) {
      processedManifestId
      confirmedAt
      confirmedPairs {
        eManifestLine
        vendorLine
      }
    }
  }
`);

export const CLEAR_VENDOR_WASTE_LINE_ALIGNMENT = gql(`
  mutation ClearVendorWasteLineAlignment($processedManifestId: ID!) {
    clearVendorWasteLineAlignment(processedManifestId: $processedManifestId) {
      processedManifestId
      confirmedAt
    }
  }
`);

/** One value with the field it came from, marked when the two sources disagree. */
function Chip({ label, text, differs }: { label: string; text: string; differs: boolean }) {
  return (
    <Box component="span" data-chip={differs ? "differs" : ""}>
      <Box component="span" data-chip-label>
        {label}
      </Box>
      {text}
    </Box>
  );
}

/**
 * Every field the source recorded, labelled, with the ones the two sources
 * disagree about marked. The reader who turns this on wants to see what they
 * are matching rather than the four values that distinguish it, so nothing is
 * trimmed and the shipping description wraps instead of truncating.
 */
function LineDetailValues({
  line,
  against,
}: {
  line: AlignmentLine;
  against: AlignmentLine | undefined;
}) {
  return (
    <Box data-fields>
      {LINE_DETAIL_FIELDS.map((field) => (
        <Fragment key={field.key}>
          <Typography variant="caption" data-field-label>
            {field.label}
          </Typography>
          <Typography
            variant="body2"
            data-field-value={
              against && field.value(line) !== field.value(against) ? "differs" : ""
            }
          >
            {field.value(line)}
          </Typography>
        </Fragment>
      ))}
    </Box>
  );
}

/** A line's values, marked against the line it is paired with. */
function LineValues({
  line,
  against,
  detailed,
}: {
  line: AlignmentLine;
  against: AlignmentLine | undefined;
  detailed: boolean;
}) {
  const differing = differingFields(line, against);
  if (detailed) return <LineDetailValues line={line} against={against} />;
  return (
    <Box data-chips>
      {PAIR_FIELDS.filter((field) => field.value(line) !== "—").map((field) => (
        <Chip
          key={field.key}
          label={field.label}
          text={field.value(line)}
          differs={differing.has(field.key)}
        />
      ))}
    </Box>
  );
}

/** agree when every value matches, differ when one does not, gap when there is no counterpart. */
function pairState(e: AlignmentLine, v: AlignmentLine | undefined): "agree" | "differ" | "gap" {
  if (!v) return "gap";
  return differingFields(e, v).size === 0 ? "agree" : "differ";
}

/** The half of a row where a source has no line at all. */
function EmptyCell({ children }: { children: React.ReactNode }) {
  return (
    <Box data-cell="empty">
      <Typography variant="caption" color="text.disabled">
        {children}
      </Typography>
    </Box>
  );
}

/**
 * A vendor line as something to pick up.
 *
 * Keyed and measured by vendor line number, so putting it on a different
 * e-Manifest line travels from wherever it sat before to level with that line.
 */
function VendorCard({
  vLine,
  against,
  state,
  detailed,
  dnd,
  registerVendor,
}: {
  vLine: AlignmentLine;
  against: AlignmentLine | undefined;
  state: "agree" | "differ" | "gap";
  detailed: boolean;
  dnd: PairingDnd;
  registerVendor: (key: string) => (node: HTMLElement | null) => void;
}) {
  return (
    <Box
      data-cell={state}
      data-drag={dnd.dragging === vLine.wasteLineNumber ? "dragging" : ""}
      draggable
      onDragStart={(event) => dnd.startDrag(event, vLine.wasteLineNumber)}
      onDragEnd={dnd.endDrag}
      ref={registerVendor(vLine.wasteLineNumber)}
    >
      <Typography variant="body2" data-line>
        <DragIndicator data-grip />
        Vendor {vLine.wasteLineNumber}
      </Typography>
      <LineValues line={vLine} against={against} detailed={detailed} />
    </Box>
  );
}

/**
 * The vendor half of a row, as a place to drop a card.
 *
 * A wrapper rather than the card itself: the slot has to stay a target while
 * it is empty, which is exactly when an e-Manifest line has nothing to pair
 * with and the reviewer is looking for somewhere to put a card.
 */
function DropSlot({
  target,
  label,
  dnd,
  children,
}: {
  target: string;
  label: string;
  dnd: PairingDnd;
  children: React.ReactNode;
}) {
  return (
    <Box
      data-slot={dnd.over === target ? "over" : ""}
      role="group"
      aria-label={label}
      onDragEnter={(event) => dnd.dragOver(event, target)}
      onDragOver={(event) => dnd.dragOver(event, target)}
      onDragLeave={() => dnd.dragLeave(target)}
      onDrop={(event) => dnd.drop(event, target)}
    >
      {children}
    </Box>
  );
}

function VendorPicker({
  eLine,
  chosen,
  vendorLines,
  duplicate,
  disabled,
  onChange,
}: {
  eLine: AlignmentLine;
  chosen: string;
  vendorLines: readonly AlignmentLine[];
  duplicate: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const pick = (value: string) => {
    setAnchor(null);
    onChange(value);
  };

  return (
    <>
      <IconButton
        size="small"
        data-pick
        disabled={disabled}
        color={duplicate ? "error" : "default"}
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={anchor != null}
        /*
         * This is the keyboard path onto the same decision the drag makes, so
         * it has to say which line it is for.
         */
        aria-label={`Vendor line for waste line ${eLine.wasteLineNumber}`}
      >
        <ArrowDropDown />
      </IconButton>
      <Menu anchorEl={anchor} open={anchor != null} onClose={() => setAnchor(null)}>
        <MenuItem selected={chosen === NO_VENDOR_LINE} onClick={() => pick(NO_VENDOR_LINE)}>
          None
        </MenuItem>
        {vendorLines.map((v) => (
          <MenuItem
            key={v.wasteLineNumber}
            selected={chosen === v.wasteLineNumber}
            onClick={() => pick(v.wasteLineNumber)}
          >
            Vendor {v.wasteLineNumber}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/** One e-Manifest line, the control, and the vendor line it is matched to. */
function PairRow({
  eLine,
  vLine,
  chosen,
  vendorLines,
  duplicate,
  disabled,
  detailed,
  dnd,
  registerVendor,
  onChange,
}: {
  eLine: AlignmentLine;
  vLine: AlignmentLine | undefined;
  chosen: string;
  vendorLines: readonly AlignmentLine[];
  duplicate: boolean;
  disabled: boolean;
  detailed: boolean;
  dnd: PairingDnd;
  registerVendor: (key: string) => (node: HTMLElement | null) => void;
  onChange: (value: string) => void;
}) {
  const state = pairState(eLine, vLine);

  return (
    <Box data-row>
      <Box data-cell={state}>
        <Box data-line-head>
          <Typography variant="body2" data-line>
            Manifest {eLine.wasteLineNumber}
          </Typography>
          <VendorPicker
            eLine={eLine}
            chosen={chosen}
            vendorLines={vendorLines}
            duplicate={duplicate}
            disabled={disabled}
            onChange={onChange}
          />
        </Box>
        <LineValues line={eLine} against={vLine} detailed={detailed} />
        {!detailed && eLine.description && (
          <Typography data-desc title={eLine.description}>
            {eLine.description}
          </Typography>
        )}
      </Box>

      <DropSlot
        target={eLine.wasteLineNumber}
        label={`Vendor line paired with waste line ${eLine.wasteLineNumber}`}
        dnd={dnd}
      >
        {vLine ? (
          <VendorCard
            vLine={vLine}
            against={eLine}
            state={duplicate ? "differ" : state}
            detailed={detailed}
            dnd={dnd}
            registerVendor={registerVendor}
          />
        ) : (
          <EmptyCell>Nothing pairs with Manifest {eLine.wasteLineNumber}</EmptyCell>
        )}
      </DropSlot>
    </Box>
  );
}

/** A vendor line nothing claimed, at the point in the vendor's numbering where it falls. */
function VendorOnlyRow({
  vLine,
  detailed,
  dnd,
  registerVendor,
}: {
  vLine: AlignmentLine;
  detailed: boolean;
  dnd: PairingDnd;
  registerVendor: (key: string) => (node: HTMLElement | null) => void;
}) {
  return (
    <Box data-row>
      <EmptyCell>No Manifest {vLine.wasteLineNumber}</EmptyCell>
      <VendorCard
        vLine={vLine}
        against={undefined}
        state="gap"
        detailed={detailed}
        dnd={dnd}
        registerVendor={registerVendor}
      />
    </Box>
  );
}

/**
 * Where a vendor line goes to pair with nothing.
 *
 * Always on screen, including when nothing is unpaired: a target that appears
 * only once something has already been dropped on it is one nobody can aim at.
 * It holds no cards. An unpaired vendor line goes back to a row of its own at
 * the point in the vendor's numbering where it falls, which is what makes a
 * dropped line legible, so parking it down here instead would hide the very
 * thing the screen exists to show.
 */
function UnpairedTray({ count, dnd }: { count: number; dnd: PairingDnd }) {
  return (
    <Box
      data-tray={dnd.over === UNPAIRED_TRAY ? "over" : ""}
      role="group"
      aria-label="Unpaired vendor lines"
      onDragEnter={(event) => dnd.dragOver(event, UNPAIRED_TRAY)}
      onDragOver={(event) => dnd.dragOver(event, UNPAIRED_TRAY)}
      onDragLeave={() => dnd.dragLeave(UNPAIRED_TRAY)}
      onDrop={(event) => dnd.drop(event, UNPAIRED_TRAY)}
    >
      <Typography variant="caption" color="text.secondary">
        {count === 0
          ? "Unpaired vendor lines — drop one here to pair it with nothing."
          : `Unpaired vendor lines: ${count}, each in a row of its own above. Drop another here to unpair it.`}
      </Typography>
    </Box>
  );
}

/**
 * Fills the pending choices from the content-based suggestion. Only proposes:
 * nothing saves until the reviewer presses Save matching, and Reset still goes
 * back to where they started.
 */
/** What Auto-match would do, and when it can do nothing, why not. */
function autoMatchHint(suggestedCount: number, unique: boolean): string {
  if (suggestedCount === 0) {
    return "The waste line values name no pairing worth proposing on this manifest.";
  }
  const confidence = unique
    ? "The values name exactly one pairing."
    : "A best guess: the values leave more than one pairing open.";
  return `${confidence} Nothing is saved until you press Save matching.`;
}

function AutoMatchButton({
  state,
  busy,
  onApply,
}: {
  state: AlignmentState;
  busy: boolean;
  onApply: (pairs: NonNullable<AlignmentState["suggestedPairs"]>) => void;
}) {
  const suggested = state.suggestedPairs ?? [];
  return (
    <Tooltip title={autoMatchHint(suggested.length, state.suggestionIsUnique)}>
      {/*
       * A disabled button fires no mouse events, so the tooltip has nothing to
       * sit on without a wrapper that stays enabled — and the reason it is
       * disabled is exactly what the tooltip is there to say.
       */}
      <span>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AutoAwesome />}
          disabled={busy || suggested.length === 0}
          onClick={() => onApply(suggested)}
        >
          Auto-match
        </Button>
      </span>
    </Tooltip>
  );
}

/** The colour key. Three states, so it is shorter to show than to describe. */
function PairingLegend() {
  return (
    <Box sx={pairingLegendSx}>
      {(
        [
          ["agree", "Matched, every value agrees"],
          ["differ", "Matched, something differs"],
          ["gap", "No counterpart"],
        ] as const
      ).map(([state, label]) => (
        <Box key={state} data-key>
          <Box data-swatch={state} />
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function PairingList({
  state,
  chosen,
  duplicates,
  busy,
  detailed,
  onDetailedChange,
  onChange,
  onPlace,
  onUnpair,
}: {
  state: AlignmentState;
  chosen: PairingChoices;
  duplicates: ReadonlySet<string>;
  busy: boolean;
  /** Showing every field a source recorded rather than the four a pairing turns on. */
  detailed: boolean;
  onDetailedChange: (next: boolean) => void;
  onChange: (eLine: string, value: string) => void;
  onPlace: (vendorLine: string, eLine: string) => void;
  onUnpair: (vendorLine: string) => void;
}) {
  const registerVendor = useReorderAnimation();
  const dnd = useWasteLinePairingDnd({ disabled: busy, onPlace, onUnpair });

  return (
    <Box sx={pairingListSx} data-detailed={detailed ? "detailed" : ""}>
      <Box data-toolbar>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={detailed}
              onChange={(event) => onDetailedChange(event.target.checked)}
            />
          }
          label="Show full line details"
        />
      </Box>

      <Box data-row data-head>
        <Typography variant="caption">e-Manifest · {state.eManifestLines.length} lines</Typography>
        <Typography variant="caption">Vendor · {state.vendorLines.length} lines</Typography>
      </Box>

      {pairingRows(state, chosen).map((row) =>
        row.kind === "vendorOnly" ? (
          <VendorOnlyRow
            key={`v-${row.vLine.wasteLineNumber}`}
            vLine={row.vLine}
            detailed={detailed}
            dnd={dnd}
            registerVendor={registerVendor}
          />
        ) : (
          <PairRow
            key={`e-${row.eLine.wasteLineNumber}`}
            eLine={row.eLine}
            vLine={row.vLine}
            chosen={chosen[row.eLine.wasteLineNumber] ?? NO_VENDOR_LINE}
            vendorLines={state.vendorLines}
            duplicate={duplicates.has(chosen[row.eLine.wasteLineNumber] ?? "")}
            disabled={busy}
            detailed={detailed}
            dnd={dnd}
            registerVendor={registerVendor}
            onChange={(value) => onChange(row.eLine.wasteLineNumber, value)}
          />
        )
      )}

      <UnpairedTray count={unclaimedVendorLines(state, chosen).length} dnd={dnd} />
    </Box>
  );
}

/** Everything between the dialog's title and its buttons. */
function MatchingBody({
  state,
  loaded,
  chosen,
  duplicates,
  busy,
  detailed,
  onDetailedChange,
  onChange,
  onPlace,
  onUnpair,
}: {
  state: AlignmentState | undefined;
  loaded: boolean;
  chosen: PairingChoices;
  duplicates: ReadonlySet<string>;
  busy: boolean;
  /** Showing every field a source recorded rather than the four a pairing turns on. */
  detailed: boolean;
  onDetailedChange: (next: boolean) => void;
  onChange: (eLine: string, value: string) => void;
  onPlace: (vendorLine: string, eLine: string) => void;
  onUnpair: (vendorLine: string) => void;
}) {
  const leftovers = state ? unclaimedVendorLines(state, chosen) : [];

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        The two sources list a different number of waste lines, so a line number no longer names the
        same waste on both sides.
      </Typography>

      <Typography variant="body2" color="text.secondary">
        Drag a vendor line onto the e-Manifest line it belongs to, or pick it from the menu on that
        line. Both do the same thing.
      </Typography>

      {state?.confirmedAt != null && (
        <Typography variant="caption" color="text.secondary">
          Matched on {new Date(state.confirmedAt).toLocaleString("en-US")}.
        </Typography>
      )}

      {duplicates.size > 0 && (
        <Alert severity="warning">
          A vendor line can match only one e-Manifest line. Used more than once:{" "}
          {[...duplicates].map((n) => `line ${n}`).join(", ")}.
        </Alert>
      )}

      {!loaded && <Skeleton variant="rectangular" height={220} />}

      {loaded && state == null && (
        <Typography variant="body2" color="text.secondary">
          The waste lines could not be loaded, so the matching cannot be edited.
        </Typography>
      )}

      {state != null && (
        <PairingList
          state={state}
          chosen={chosen}
          duplicates={duplicates}
          busy={busy}
          detailed={detailed}
          onDetailedChange={onDetailedChange}
          onChange={onChange}
          onPlace={onPlace}
          onUnpair={onUnpair}
        />
      )}

      {state != null && <PairingLegend />}

      {leftovers.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          Saving records {leftovers.map((v) => `vendor line ${v.wasteLineNumber}`).join(", ")} as
          having no e-Manifest counterpart.
        </Typography>
      )}
    </Stack>
  );
}

/**
 * Which vendor waste line is which e-Manifest waste line.
 *
 * The two sources list different numbers of waste lines, so a line number no
 * longer names the same waste on both sides and every per-line comparison is
 * between two unrelated wastes. This is where a person says which is which.
 *
 * Its own dialog rather than a block inside a row: the decision is about the
 * whole manifest, it needs both sides of every line on screen at once, and the
 * findings it clears are spread across the list it would otherwise sit in.
 *
 * Two ways to say it, and neither is the only one. A vendor line is dragged to
 * the e-Manifest line it belongs to, which is the shape of the thought; native
 * drag and drop has no keyboard equivalent, so every row keeps a menu that
 * records the same answer. The drag hands back whatever it displaced, where
 * the menu answers one line at a time and leaves the clash on screen.
 *
 * Auto-match proposes a pairing from the waste line values and never applies
 * it on its own; the reviewer still presses Save. Only about a quarter of live
 * manifests carrying this produce a content alignment that is both unique and
 * fully agreeing, so the button says when its guess is not the only possible
 * answer rather than presenting every guess as settled.
 */
export function WasteLinePairingDialog({
  target,
  onClose,
  onSaved,
  onRemoved,
}: {
  /** The manifest being matched, or null when the dialog is closed. */
  target: WasteLineMatchingTarget | null;
  onClose: () => void;
  /** Told which manifest, so the caller can say the findings on it are now stale. */
  onSaved?: (processedManifestId: string) => void;
  /**
   * Told which manifest had its matching removed. Distinct from `onSaved`:
   * removing a pairing puts the manifest back on line-number matching, which
   * is the opposite of what `onSaved`'s callers do with a "just matched"
   * manifest.
   */
  onRemoved?: (processedManifestId: string) => void;
}) {
  const alerts = useAlerts();
  const processedManifestId = target?.processedManifestId ?? "";
  // A failed fetch leaves data undefined, so the null-state message below is
  // what reports it; there is nothing the error object adds on top of that.
  const { data, loading } = useQuery(VENDOR_WASTE_LINE_ALIGNMENT_QUERY, {
    variables: { processedManifestId },
    skip: target == null,
  });
  const [edits, setEdits] = useState<PairingChoices | null>(null);
  /*
   * Showing every field a source recorded rather than the four a pairing turns
   * on. A reading preference, not a mode: the rows keep their drag handles and
   * menus, so a reader who wants the shipping description in front of them can
   * still move a line while it is there.
   */
  const [detailed, setDetailed] = useState(false);

  // Discards a cancelled draft rather than carrying it forward: the dialog
  // stays mounted (rendering null) between manifests, so `edits` otherwise
  // survives both closing and switching to a different manifest. Adjusted
  // during render, keyed on the manifest id, rather than in an effect, so the
  // stale draft is never painted even for a frame; it does not fight the
  // post-save reset in onSave/onRemove below because a successful save leaves
  // the target's id unchanged, so this never fires then.
  const targetId = target?.processedManifestId;
  const [editsForTargetId, setEditsForTargetId] = useState(targetId);
  if (targetId !== editsForTargetId) {
    setEditsForTargetId(targetId);
    setEdits(null);
  }

  const [save, { loading: saving }] = useMutation(SET_VENDOR_WASTE_LINE_ALIGNMENT, {
    refetchQueries: [VENDOR_WASTE_LINE_ALIGNMENT_QUERY],
  });
  const [clear, { loading: clearing }] = useMutation(CLEAR_VENDOR_WASTE_LINE_ALIGNMENT, {
    refetchQueries: [VENDOR_WASTE_LINE_ALIGNMENT_QUERY],
  });

  const state = data?.vendorWasteLineAlignmentCandidates;

  // What is recorded, or pairing by line number where nothing is — the same
  // starting point the pipeline uses, so the reviewer edits what they see.
  const initial = useMemo(() => (state ? initialChoices(state) : {}), [state]);
  const chosen = edits ?? initial;
  const duplicates = useMemo(() => duplicateVendorLines(chosen), [chosen]);

  const busy = saving || clearing;

  const onSave = async () => {
    if (!state) return;
    try {
      await save({ variables: { processedManifestId, pairs: pairsToSave(state, chosen) } });
      setEdits(null);
      alerts.success(
        "Waste line matching saved. The conflicts on the matched lines clear on the next manifest run."
      );
      onSaved?.(processedManifestId);
      // Left open. The reader has just answered a question about nine waste
      // lines and the most likely next thought is about one of them, so
      // closing would make changing the answer a matter of finding the way
      // back in.
    } catch (e) {
      alerts.error(e instanceof Error ? e.message : "The matching could not be saved");
    }
  };

  const onRemove = async () => {
    try {
      await clear({ variables: { processedManifestId } });
      setEdits(null);
      alerts.success("Waste line matching removed. The lines pair by number again.");
      onRemoved?.(processedManifestId);
    } catch (e) {
      alerts.error(e instanceof Error ? e.message : "The matching could not be removed");
    }
  };

  const dirty = state
    ? state.eManifestLines.some((e) => chosen[e.wasteLineNumber] !== initial[e.wasteLineNumber])
    : false;
  // After the hooks, so their order never depends on whether it is open.
  if (!target) return null;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      title="Match waste lines"
      titleBarActionElement={
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="caption" color="text.secondary" noWrap>
            {target.manifestTrackingNumber ?? ""}
            {state
              ? ` · ${state.eManifestLines.length} e-Manifest, ${state.vendorLines.length} vendor`
              : ""}
          </Typography>
          {state != null && (
            <AutoMatchButton
              state={state}
              busy={busy}
              onApply={(pairs) => setEdits(applyPairs(state, pairs))}
            />
          )}
        </Stack>
      }
      /*
       * Through the shared Dialog's own action bar. It already renders Cancel
       * and a Save button of its own, so a DialogActions of ours here puts a
       * second row of them under the first.
       */
      additionalPrimaryActionElements={[
        /*
         * Back to what is stored, or to pairing by number where nothing is.
         * Offered whenever it would change something, so a saved answer is
         * never a dead end.
         */
        <Button key="reset" disabled={busy || !dirty} onClick={() => setEdits(null)}>
          Reset
        </Button>,
      ]}
      secondaryActionElements={
        state?.confirmedAt != null
          ? [
              <Button key="remove" color="warning" disabled={busy} onClick={() => void onRemove()}>
                Remove matching
              </Button>,
            ]
          : []
      }
      primaryActionButtonProps={{
        saveText: "Save matching",
        loading: busy,
        // state == null covers the load having failed. Without it the button is
        // offered, and pressing it returns at the guard in onSave with nothing
        // written and nothing said.
        disabled:
          busy ||
          state == null ||
          duplicates.size > 0 ||
          !canRecordPairing(dirty, state.confirmedAt),
        onClick: () => void onSave(),
      }}
    >
      <MatchingBody
        state={state}
        // Only the fetch still being in flight keeps the skeleton up. Folding
        // the error in here too left a failed load showing a skeleton forever,
        // because the message below renders on `loaded` and never got the
        // chance. A failure is loaded: there is nothing more coming.
        loaded={!loading}
        chosen={chosen}
        duplicates={duplicates}
        busy={busy}
        detailed={detailed}
        onDetailedChange={setDetailed}
        onChange={(eLine, value) => setEdits(chooseVendorLine(chosen, eLine, value))}
        onPlace={(vendorLine, eLine) => setEdits(placeVendorLine(chosen, vendorLine, eLine))}
        onUnpair={(vendorLine) => setEdits(unpairVendorLine(chosen, vendorLine))}
      />
    </Dialog>
  );
}
